import { useEffect, useState } from "react";
import {
  Button,
  Typography,
  Box,
  LinearProgress,
  CircularProgress,
  Alert,
  Checkbox,
  FormGroup,
  FormControlLabel,
} from "@mui/material";
import { Upload, CheckCircle, Download } from "@mui/icons-material";
import { uploadExcelAndGetBlob } from "../../../lib/api_routes/aiToolkit";
import { InstructionSection } from "../../AiToolkit/InstructionSection";
import { ExcelTable } from "./ExcelTestTable";
import theme from "../../../theme";
import { useAccountInfo } from "../../../hooks/useAccountInfo";
import { useActivityTracking } from "../../../hooks/userActivityTracking";
import { formatAxiosError } from "../../../utils/error_message";
import { TOOL_TYPES } from "../../../utils/aiToolkitInfo";

const useFileUpload = (accountId) => {
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [testLoading, setTestLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [uploadComplete, setUploadComplete] = useState(false);
  const [processedBlobUrl, setProcessedBlobUrl] = useState(null);
  const [processComplete, setProcessComplete] = useState(false);
  const [testBlobUrl, setTestBlobUrl] = useState(null);
  const [testComplete, setTestComplete] = useState(false);
  const [testApproved, setTestApproved] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [completedSteps, setCompletedSteps] = useState(new Set());
  const [excelData, setExcelData] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [multiPrompt, setMultiPrompt] = useState(false);
  // USER ACTIVITY TRACKING
  const { trackAction, resetSession } = useActivityTracking(
    "ai_toolkit_iterator",
    accountId,
  );

  // Update steps based on file upload
  useEffect(() => {
    if (uploadComplete && !completedSteps.has(1)) {
      setCompletedSteps((prev) => new Set([...prev, 1]));
      setCurrentStep(2);
    }
  }, [uploadComplete, completedSteps]);

  // Update steps based on processing completition
  useEffect(() => {
    if (processComplete && !completedSteps.has(2)) {
      setCompletedSteps((prev) => new Set([...prev, 2]));
      setCurrentStep(3);
    }
  }, [processComplete, completedSteps]);

  // Complete the final step
  const handleCompletedSteps = () => {
    handleDownload();
    setCompletedSteps((prev) => new Set([...prev, 3]));
  };

  // Reset all states to go back to step 1
  const goBackToStep1 = () => {
    setFile(null);
    setUploadComplete(false);
    setTestComplete(false);
    setTestApproved(false);
    setProgress(0);
    setCurrentStep(1);
    setCompletedSteps(new Set());
    setProcessedBlobUrl(null);
    setTestBlobUrl(null);
    setProcessComplete(false);
    setExcelData(null);
    setShowPreview(false);
    resetSession();

    // Reset file input
    const fileInput = document.getElementById("file-upload");
    if (fileInput) {
      fileInput.value = "";
    }
  };

  // Test the uploaded file with backend
  const testFile = async () => {
    if (!file) return;
    setTestLoading(true);

    try {
      const response = await uploadExcelAndGetBlob(file, multiPrompt, true);

      // Create BlobURL for test
      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      // Transform the data to arryBuffer to place as a table
      const arrayBuffer = await blob.arrayBuffer();
      const workbook = window.XLSX.read(arrayBuffer, { type: "array" });
      const worksheet = workbook.Sheets[workbook.SheetNames[0]];
      const processedData = window.XLSX.utils.sheet_to_json(worksheet);

      setExcelData(processedData);

      const url = window.URL.createObjectURL(blob);
      setTestBlobUrl(url);
      setTestComplete(true);
      trackAction("Process Iterator Test", "SUCCESS", null, {
        multiPrompt,
      }).catch(console.error);
    } catch (error) {
      const { serverMessage, fullError } = await formatAxiosError(error);
      alert(serverMessage ?? "Failed to process test file.");
      trackAction("Process Iterator Test", "FAILED", fullError, {
        multiPrompt,
      }).catch(console.error);
      resetSession();
    } finally {
      setTestLoading(false);
    }
  };

  // Process the uploaded file with backend
  const processFile = async () => {
    if (!file) return;
    setLoading(true);

    try {
      const response = await uploadExcelAndGetBlob(file, multiPrompt, false);

      // Create BlobURL for final processing
      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = window.URL.createObjectURL(blob);
      setProcessedBlobUrl(url);
      setProcessComplete(true);

      // USER ACTIVITY TRACKING
      trackAction("Process Iterator", "SUCCESS", null, { multiPrompt }).catch(
        console.error,
      );
    } catch (error) {
      const { serverMessage, fullError } = await formatAxiosError(error);
      alert(serverMessage ?? "Failed to process file.");
      trackAction("Process Iterator", "FAILED", fullError, {
        multiPrompt,
      }).catch(console.error);
      resetSession();
    } finally {
      setLoading(false);
    }
  };

  // Download test file
  const handleTestDownload = async () => {
    if (!testBlobUrl) return;

    const downloadFileName = "Processed_iterator_test_output.xlsx";
    const link = document.createElement("a");
    link.href = testBlobUrl;
    link.setAttribute("download", downloadFileName);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    // USER ACTIVITY TRACKING - append download processed
    trackAction("Download Test File", "SUCCESS", null, {
      fileName: file?.name,
      downloadFileName,
    }).catch(console.error);
  };

  // Download processed file
  const handleDownload = async () => {
    if (!processedBlobUrl) return;

    const downloadFileName = "Processed_iterator_output.xlsx";

    const link = document.createElement("a");
    link.href = processedBlobUrl;
    link.setAttribute("download", downloadFileName);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    // USER ACTIVITY TRACKING - append download processed
    trackAction("Download Processed File", "SUCCESS", null, {
      fileName: file?.name,
      downloadFileName,
    }).catch(console.error);
  };

  // Handle test approval
  const handleTestApproval = (approved) => {
    if (approved) {
      setTestApproved(true);
      setShowPreview(false);
    } else {
      // User wants to go back - reset everything
      goBackToStep1();
    }
  };

  const handleFileUpload = (event) => {
    const ALLOWED_EXTENSIONS = ["xlsx"];
    const UPLOAD_PROGRESS_INTERVAL = 200;
    const UPLOAD_PROGRESS_STEP = 20;

    const uploadedFile = event.target.files[0];
    if (!uploadedFile) return;

    const allowedExtensions = ALLOWED_EXTENSIONS;
    const fileExtension = uploadedFile.name.split(".").pop().toLowerCase();

    if (!allowedExtensions.includes(fileExtension)) {
      alert("Only .xlsx files are allowed!");
      return;
    }

    // Reset all states for new upload
    setFile(uploadedFile);
    setUploadComplete(false);
    setProcessComplete(false);
    setTestComplete(false);
    setTestApproved(false);
    setProgress(0);
    setProcessedBlobUrl(null);
    setTestBlobUrl(null);

    // Simulate progress bar
    setLoading(true);
    let progressInterval = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 100) {
          clearInterval(progressInterval);
          setUploadComplete(true);
          setLoading(false);
          return 100;
        }
        return prev + UPLOAD_PROGRESS_STEP;
      });
    }, UPLOAD_PROGRESS_INTERVAL);
  };

  return {
    file,
    loading,
    excelData,
    testLoading,
    progress,
    uploadComplete,
    processComplete,
    testComplete,
    testApproved,
    currentStep,
    completedSteps,
    showPreview,
    multiPrompt,
    setMultiPrompt,
    setShowPreview,
    testFile,
    handleFileUpload,
    processFile,
    handleDownload,
    handleTestDownload,
    handleCompletedSteps,
    handleTestApproval,
    goBackToStep1,
  };
};

const FileProgressIndicator = ({
  fileName,
  progress,
  uploadComplete,
  disabled,
}) => {
  return (
    <Box sx={{ mt: 1 }}>
      <Typography variant="body2" sx={{ color: "text.secondary", mb: 1 }}>
        {fileName}
      </Typography>
      <LinearProgress
        variant="determinate"
        value={progress}
        sx={{
          height: 8,
          borderRadius: 4,
          backgroundColor: theme.palette.gray.medium,
          "& .MuiLinearProgress-bar": {
            backgroundColor: disabled
              ? theme.palette.gray.medium
              : uploadComplete,
          },
        }}
      />
    </Box>
  );
};

const FileUploadForm = ({ handleFileUpload, uploadComplete, disabled }) => {
  return (
    <>
      <input
        type="file"
        id="file-upload"
        style={{ display: "none" }}
        onChange={handleFileUpload}
        accept=".xlsx"
      />
      <label htmlFor="file-upload">
        <Button
          component="span"
          variant="contained"
          fullWidth
          startIcon={uploadComplete ? <CheckCircle /> : <Upload />}
          sx={{
            backgroundColor: disabled
              ? theme.palette.gray.dark
              : theme.palette.primary.main,
            "&:hover": {
              backgroundColor: theme.palette.primary.main,
            },
            mb: 1,
          }}
        >
          {uploadComplete ? "Upload Complete" : "Upload File"}
        </Button>
      </label>
    </>
  );
};

const TestOrProcessSection = ({
  disabled,
  onTest,
  onProcess,
  testLoading,
  processLoading,
  testComplete,
  onTestDownload,
  onTestApproval,
  testApproved,
  onGoBack,
  showPreview,
  setShowPreview,
  multiPrompt,
  setMultiPrompt,
}) => {
  // Handle checkbox changes
  const handlePromptChange = (isSinglePrompt) => {
    if (isSinglePrompt) {
      setMultiPrompt(false);
    } else {
      setMultiPrompt(true);
    }
  };

  return (
    <Box
      sx={{
        my: 1,
      }}
    >
      <Typography
        variant="body1"
        sx={{ color: "text.primary", my: 1.5, textAlign: "center" }}
      >
        Please select the way you want to run the prompt.
      </Typography>

      <FormGroup
        sx={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "center",
          my: 2,
        }}
      >
        <FormControlLabel
          control={
            <Checkbox
              checked={!multiPrompt}
              onChange={() => handlePromptChange(true)}
              disabled={disabled || testComplete}
              disableRipple
            />
          }
          label="Single Prompt"
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={multiPrompt}
              onChange={() => handlePromptChange(false)}
              disabled={disabled || testComplete}
              disableRipple
            />
          }
          label="Multi Prompt"
        />
      </FormGroup>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
        {/* Test Button */}
        <Button
          disabled={disabled || testComplete}
          onClick={onTest}
          variant="contained"
          sx={{
            width: "100%",
            backgroundColor: testComplete
              ? theme.palette.secondary.lawnGreen
              : theme.palette.primary.brandSecondary,
            // "&:hover": {
            //   backgroundColor: testComplete
            //     ? theme.palette.secondary.lawnGreen
            //     : theme.palette.primary.main,
            // },
            "&:disabled": { backgroundColor: theme.palette.gray.medium },
          }}
          startIcon={
            testLoading ? (
              <CircularProgress size={20} color="inherit" />
            ) : testComplete ? (
              <CheckCircle />
            ) : null
          }
        >
          {testLoading
            ? "Testing..."
            : testComplete
              ? "Test Complete"
              : "Test File"}
        </Button>

        {/* Test Results */}
        {testComplete && (
          <Box sx={{ mt: 2 }}>
            <Alert
              severity="success"
              sx={{ mb: 2, alignItems: "center" }}
              icon={<CheckCircle />}
            >
              <Typography variant="body1" component="div">
                File Processed Successfully!
              </Typography>
            </Alert>

            <Button
              onClick={onTestDownload}
              variant="outlined"
              startIcon={<Download />}
              disabled={disabled}
              sx={{
                width: "100%",
                mb: 2,
                backgroundColor: theme.palette.primary.main,
                color: theme.palette.primary.white,
                "&:hover": {
                  backgroundColor: theme.palette.primary.main,
                },
                "&:disabled": { backgroundColor: theme.palette.gray.medium },
              }}
            >
              Download Test File
            </Button>

            <Button
              onClick={() => setShowPreview(!showPreview)}
              variant="outlined"
              disabled={disabled}
              sx={{
                width: "100%",
                mb: 2,
                backgroundColor: theme.palette.primary.white,
                border: `1px solid ${theme.palette.primary.brandSecondary}`,
                color: theme.palette.primary.brandSecondary,
                "&:hover": {
                  backgroundColor: theme.palette.secondary.lightBlack,
                },
                "&:disabled": { backgroundColor: theme.palette.gray.light },
              }}
            >
              {showPreview ? "Hide Preview" : "Preview Test"}
            </Button>

            {/* Feedback Section */}
            <Box
              sx={{
                my: 2,
              }}
            >
              <Typography
                variant="body1"
                sx={{ pb: 3, color: "text.primary", textAlign: "center" }}
              >
                Are you happy with the output?
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  width: "100%",
                  gap: 2,
                }}
              >
                <Button
                  onClick={() => onTestApproval(true)}
                  disabled={disabled}
                  fullWidth
                  variant="contained"
                  size="small"
                  sx={{
                    backgroundColor: theme.palette.primary.brandSecondary,
                    "&:hover": {
                      backgroundColor: theme.palette.primary.brandSecondary,
                    },
                  }}
                >
                  Yes!
                </Button>
                <Button
                  onClick={() => onTestApproval(false)}
                  disabled={disabled}
                  fullWidth
                  variant="contained"
                  size="small"
                  sx={{
                    border: `1px solid ${theme.palette.primary.main}`,
                    color: theme.palette.primary.main,
                    boxShadow: "none",
                    backgroundColor: theme.palette.primary.white,
                    "&:hover": {
                      backgroundColor: theme.palette.primary.main,
                      color: theme.palette.primary.white,
                    },
                    "&:disabled": {
                      backgroundColor: theme.palette.gray.ligh,
                      border: "transparent",
                    },
                  }}
                >
                  No! Go Back
                </Button>
              </Box>
            </Box>
          </Box>
        )}

        {/* Process Button - only enabled after test approval or if no test was done */}
        <Button
          disabled={
            disabled || processLoading || (testComplete && !testApproved)
          }
          onClick={onProcess}
          variant="contained"
          sx={{
            width: "100%",
            backgroundColor: theme.palette.primary.main,
            "&:hover": {
              backgroundColor: theme.palette.primary.main,
            },
            "&:disabled": {
              backgroundColor: theme.palette.gray.dark,
              color: theme.palette.primary.white,
            },
            mt: testComplete ? 1 : 0,
          }}
          startIcon={
            processLoading ? (
              <CircularProgress size={20} color="inherit" />
            ) : null
          }
        >
          {processLoading ? "Processing..." : "Process File"}
        </Button>

        {/* Go back Button - if user want to change the download file */}
        {!testComplete && (
          <Button
            disabled={disabled || processLoading}
            onClick={onGoBack}
            variant="contained"
            sx={{
              width: "100%",
              backgroundColor: theme.palette.primary.white,
              border: `1px solid ${theme.palette.primary.main}`,
              color: theme.palette.primary.main,
              boxShadow: "none",
              "&:hover": {
                backgroundColor: theme.palette.primary.medium,
                color: theme.palette.primary.brandSecondary,
                border: `1px solid ${theme.palette.primary.brandSecondary}`,
              },
              "&:disabled": {
                backgroundColor: theme.palette.gray.light,
                border: "none",
              },
            }}
          >
            Go Back
          </Button>
        )}
      </Box>
    </Box>
  );
};

const ProcessingCompletedScreen = ({ handleDownload }) => {
  return (
    <Box sx={{ mt: 3 }}>
      <Alert
        severity="success"
        sx={{ mb: 2, alignItems: "center" }}
        icon={<CheckCircle />}
      >
        <Typography variant="subtitle1" component="div">
          File Processed Successfully!
        </Typography>
        <Typography variant="body2">
          Your file has been processed and is ready for download.
        </Typography>
      </Alert>
      <Button
        onClick={handleDownload}
        variant="contained"
        startIcon={<Download />}
        sx={{
          backgroundColor: theme.palette.primary.main,
          "&:hover": { backgroundColor: theme.palette.primary.main },
        }}
        fullWidth
      >
        Download Processed File
      </Button>
    </Box>
  );
};

export function IteratorForm() {
  // Account Info from hooks
  const { accountInfo } = useAccountInfo();
  const {
    file,
    excelData,
    loading,
    testLoading,
    progress,
    uploadComplete,
    currentStep,
    completedSteps,
    processComplete,
    testComplete,
    testApproved,
    showPreview,
    multiPrompt,
    setMultiPrompt,
    setShowPreview,
    handleCompletedSteps,
    handleFileUpload,
    testFile,
    processFile,
    handleTestDownload,
    handleTestApproval,
    goBackToStep1,
  } = useFileUpload(accountInfo?.accountId);

  return (
    <>
      <Typography variant="h4" sx={{ mb: 4, py: 2, textAlign: "center" }}>
        Iteration Process
      </Typography>
      <InstructionSection
        currentStep={currentStep}
        completedSteps={completedSteps}
        toolType={TOOL_TYPES.ITERATOR}
      />
      <Box
        sx={{
          m: "auto",
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            sm: "1fr",
            md: "repeat(3, 1fr)",
          },
          alignItems: "stretch",
          justifyContent: "center",
          margin: "auto",
          gap: 2,
          p: 2,
          my: 4,
          maxWidth: {
            xs: "400px",
            sm: "420px",
            md: "75rem",
          },
        }}
      >
        {/* Step 1: File Upload Section */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
            p: 2,
            height: "100%",
            backgroundColor: theme.palette.primary.lightBackground,
            borderRadius: 2,
            opacity: currentStep === 1 ? 1 : completedSteps.has(1) ? 0.6 : 0.4,
            pointerEvents: currentStep === 1 ? "auto" : "none",
            order: { xs: 1, md: 1 },
          }}
        >
          <Typography variant="h6" sx={{ textAlign: "center" }}>
            Upload Your Excel File
          </Typography>
          <FileUploadForm
            handleFileUpload={handleFileUpload}
            disabled={currentStep !== 1}
          />
          {file && (
            <FileProgressIndicator
              fileName={file.name}
              progress={progress}
              uploadComplete={uploadComplete}
              disabled={currentStep !== 1}
            />
          )}
        </Box>

        {/* Step 2: Testing/Processing Section */}
        {uploadComplete && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              p: 2,
              height: "100%",
              backgroundColor: theme.palette.primary.lightBackground,
              borderRadius: 2,
              opacity:
                currentStep === 2 ? 1 : completedSteps.has(2) ? 0.6 : 0.4,
              pointerEvents: currentStep === 2 ? "auto" : "none",
              order: { xs: 2, md: 2 },
            }}
          >
            <Typography variant="h6" sx={{ textAlign: "center" }}>
              Test or Process File
            </Typography>
            <TestOrProcessSection
              disabled={currentStep !== 2 || !file}
              onTest={testFile}
              onProcess={processFile}
              testLoading={testLoading}
              processLoading={loading}
              testComplete={testComplete}
              onTestDownload={handleTestDownload}
              onTestApproval={handleTestApproval}
              testApproved={testApproved}
              onGoBack={goBackToStep1}
              showPreview={showPreview}
              setShowPreview={setShowPreview}
              multiPrompt={multiPrompt}
              setMultiPrompt={setMultiPrompt}
            />
          </Box>
        )}

        {/* Step 3: Download Results Section */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
            p: 2,
            height: "100%",
            backgroundColor: theme.palette.primary.lightBackground,
            borderRadius: 2,
            opacity: currentStep === 3 ? 1 : 0.4,
            pointerEvents: currentStep === 3 ? "auto" : "none",
            order: { xs: 3, md: 3 },
          }}
        >
          {processComplete && (
            <>
              <Typography variant="h6" sx={{ textAlign: "center" }}>
                Download Results
              </Typography>
              <ProcessingCompletedScreen
                handleDownload={handleCompletedSteps}
                disabled={currentStep !== 3}
              />
            </>
          )}
        </Box>
      </Box>

      {currentStep === 2 &&
        showPreview &&
        testComplete &&
        !testApproved &&
        Array.isArray(excelData) &&
        excelData.length > 0 && (
          <Box
            sx={{
              px: 2,
              my: 4,
              mx: "auto",
              maxWidth: "75rem",
            }}
          >
            <ExcelTable excelData={excelData} maxLength={100} />
          </Box>
        )}
    </>
  );
}
